import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import '../scss/loyalty-rules-section.component.scss';
import { LOCALES } from '../../common/constants';
import { formatAmount } from '../helper/common.helper';
import { ironMedal, bronzeMedal, silverMedal, goldMedal, platinumMedal } from './images/jpg/medalimages';

/* real tier medals (same as the milestone cards) — Iron, Bronze, Silver, Gold, Platinum */
const TIER_MEDALS: React.ReactNode[] = [ironMedal, bronzeMedal, silverMedal, goldMedal, platinumMedal];

const EARN_ICONS: React.ReactNode[] = [
    (
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <path d="M7 3h7l5 5v13H7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M9.5 13h5M9.5 16.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    (
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3.5 20a5.5 5.5 0 0 1 11 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M18.5 8v6M15.5 11h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 3l2.5 5.4 5.9.6-4.4 3.9 1.3 5.8L12 15.9 6.7 19.3l1.3-5.8L3.6 9.6l5.9-.6z" />
        </svg>
    ),
    (
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <rect x="4" y="9" width="16" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3 9h18M12 9v11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M12 9S10.5 4 8 4a2 2 0 0 0 0 5.5zM12 9s1.5-5 4-5a2 2 0 0 1 0 5.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
    ),
];

const LoyaltyRulesSection: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState('howToEarn');
    const currentLanguage = i18n.language;

    /* ---- sliding pill indicator + skeleton-on-switch ---- */
    const pillsRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });
    const [indicatorReady, setIndicatorReady] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);

    const measureIndicator = () => {
        const container = pillsRef.current;
        if (!container) return;
        const activeBtn = container.querySelector<HTMLButtonElement>('.rules-pill-btn.active');
        if (activeBtn) {
            setIndicator({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
        }
    };

    // position the indicator BEFORE paint (no flash / no slide-in on first mount)
    useLayoutEffect(() => {
        measureIndicator();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, currentLanguage]);

    // keep it aligned on resize
    useEffect(() => {
        window.addEventListener('resize', measureIndicator);
        return () => window.removeEventListener('resize', measureIndicator);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // enable the slide transition only after first paint
    useEffect(() => {
        const id = requestAnimationFrame(() => setIndicatorReady(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const handleTabChange = (key: string) => {
        if (key === activeTab) return;
        setActiveTab(key);
        setIsSwitching(true);
        window.setTimeout(() => setIsSwitching(false), 480);
    };

    const renderSkeleton = () => (
        <div className="rules-panel rules-skeleton" aria-hidden="true">
            <div className="sk sk-title" />
            <div className="sk sk-sub" />
            <div className="sk-rows">
                <div className="sk sk-row" />
                <div className="sk sk-row" />
                <div className="sk sk-row" />
                <div className="sk sk-row" />
            </div>
        </div>
    );
    const isEnglish = currentLanguage === LOCALES.EN_US;
    const accountType = useSelector((state: any) => state.auth?.userOnboardingData?.accountType);
    const tabs = [
        {
            key: 'howToEarn',
            label: 'LOYALTY_PROGRAM_HOW_TO_EARN_POINTS',
            mobileLabel: 'LOYALTY_PROGRAM_HOW_TO_EARN_POINTS_MOBILE'
        },
        {
            key: 'loyaltyTiers',
            label: 'LOYALTY_PROGRAM_LOYALTY_TIERS_REWARDS',
            mobileLabel: 'LOYALTY_PROGRAM_LOYALTY_TIERS_REWARDS_MOBILE'
        },
        {
            key: 'longTerm',
            label: 'LOYALTY_PROGRAM_LONG_TERM_REWARDS',
            mobileLabel: 'LOYALTY_PROGRAM_LONG_TERM_REWARDS_MOBILE'
        },
        {
            key: 'rules',
            label: 'LOYALTY_PROGRAM_IMPORTANT_RULES',
            mobileLabel: 'LOYALTY_PROGRAM_IMPORTANT_RULES_MOBILE'
        }
    ];

    const earningActivitiesType1 = [
        {
            activity: 'LOYALTY_PROGRAM_INVOICING_THROUGH_INVOZIO',
            points: 'LOYALTY_PROGRAM_POINTS_PER_SEK_INVOICED',
            notes: 'LOYALTY_PROGRAM_POINTS_AWARDED_AFTER_PAYMENT'
        },
        {
            activity: 'LOYALTY_PROGRAM_REFERRING_NEW_FREELANCER',
            points: 'LOYALTY_PROGRAM_POINTS_25000',
            notes: 'LOYALTY_PROGRAM_REFERRED_USER_MINIMUM_INVOICE'
        },
        {
            activity: 'LOYALTY_PROGRAM_POSTING_REVIEW_GOOGLE_TRUSTPILOT',
            points: 'LOYALTY_PROGRAM_POINTS_25000_PER_REVIEW',
            notes: 'LOYALTY_PROGRAM_MAX_2_REVIEWS_PER_USER'
        },
        {
            activity: 'LOYALTY_PROGRAM_CELEBRATING_BIRTHDAY',
            points: 'LOYALTY_PROGRAM_POINTS_25000_BIRTHDAY',
            notes: 'LOYALTY_PROGRAM_ONCE_PER_CALENDAR_YEAR'
        }
    ];

    const earningActivitiesType2 = [
        {
            activity: 'LOYALTY_PROGRAM_INVOICING_THROUGH_INVOZIO',
            points: 'LOYALTY_PROGRAM_POINTS_PER_SEK_INVOICED_TYPE2',
            notes: 'LOYALTY_PROGRAM_POINTS_AWARDED_AFTER_PAYMENT_TYPE2'
        },
        {
            activity: 'LOYALTY_PROGRAM_REFERRING_NEW_BUSINESS_TYPE2',
            points: 'LOYALTY_PROGRAM_POINTS_25000_TYPE2',
            notes: 'LOYALTY_PROGRAM_REFERRED_USER_MINIMUM_INVOICE_TYPE2'
        },
        {
            activity: 'LOYALTY_PROGRAM_PUBLISHING_REVIEW_TYPE2',
            points: 'LOYALTY_PROGRAM_POINTS_25000_PER_REVIEW_TYPE2',
            notes: 'LOYALTY_PROGRAM_MAX_2_REVIEWS_PER_USER_TYPE2'
        }
    ];

    const earningActivities = accountType === 2 ? earningActivitiesType2 : earningActivitiesType1;

    const renderHowToEarnContent = () => (
        <div className="rules-panel">
            <div className="panel-head">
                <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_HOW_TO_EARN_POINTS')}</h4>
                <p className="panel-sub">{t('LOYALTY_PROGRAM_YOU_EARN_LOYALTY_POINTS_AUTOMATICALLY')}</p>
            </div>

            {/* Attractive activity list (responsive) */}
            <div className="earn-list">
                {earningActivities.map((activity, index) => (
                    <div className="earn-item" key={index}>
                        <span className="earn-item__icon" aria-hidden="true">
                            {EARN_ICONS[index % EARN_ICONS.length]}
                        </span>
                        <div className="earn-item__text">
                            <span className="earn-item__title">{t(activity.activity)}</span>
                            <span className="earn-item__note">{t(activity.notes)}</span>
                        </div>
                        <span className="earn-item__points">{t(activity.points)}</span>
                    </div>
                ))}
            </div>

            <p className="panel-note">
                <span className="note-highlight">{t('LOYALTY_PROGRAM_NOTE')}:</span> {t('LOYALTY_PROGRAM_POINT_TOTALS_ROLLING_12_MONTHS')}
            </p>
        </div>
    );

    const medalLevels = [
        { tier: 'LOYALTY_PROGRAM_IRON_LEVEL', pointsNeeded: 250000, discount: '0%', extraBenefits: '–' },
        { tier: 'LOYALTY_PROGRAM_BRONZE_LEVEL', pointsNeeded: 250000, discount: '20%', extraBenefits: 'LOYALTY_PROGRAM_BRONZE_LEVEL_DESC' },
        { tier: 'LOYALTY_PROGRAM_SILVER_LEVEL', pointsNeeded: 500000, discount: '30%', extraBenefits: 'LOYALTY_PROGRAM_SILVER_LEVEL_DESC' },
        { tier: 'LOYALTY_PROGRAM_GOLD_LEVEL', pointsNeeded: 1000000, discount: '40%', extraBenefits: 'LOYALTY_PROGRAM_GOLD_LEVEL_DESC' },
        { tier: 'LOYALTY_PROGRAM_PLATINUM_LEVEL', pointsNeeded: 2000000, discount: '50%', extraBenefits: 'LOYALTY_PROGRAM_PLATINUM_LEVEL_DESC' }
    ];

    const medalLevelsType2 = [
        { tier: 'LOYALTY_PROGRAM_IRON_LEVEL', pointsNeeded: 2000000, discount: '0%', extraBenefits: '–' },
        { tier: 'LOYALTY_PROGRAM_BRONZE_LEVEL', pointsNeeded: 2000000, discount: '20%', extraBenefits: 'LOYALTY_PROGRAM_BRONZE_BENEFITS_TYPE2' },
        { tier: 'LOYALTY_PROGRAM_SILVER_LEVEL', pointsNeeded: 5000000, discount: '30%', extraBenefits: 'LOYALTY_PROGRAM_SILVER_BENEFITS_TYPE2' },
        { tier: 'LOYALTY_PROGRAM_GOLD_LEVEL', pointsNeeded: 10000000, discount: '40%', extraBenefits: 'LOYALTY_PROGRAM_GOLD_BENEFITS_TYPE2' },
        { tier: 'LOYALTY_PROGRAM_PLATINUM_LEVEL', pointsNeeded: 20000000, discount: '50%', extraBenefits: 'LOYALTY_PROGRAM_PLATINUM_BENEFITS_TYPE2' }
    ];

    const activeMedalLevels = accountType === 2 ? medalLevelsType2 : medalLevels;

    const renderContent = () => {
        switch (activeTab) {
            case 'howToEarn':
                return renderHowToEarnContent();
            case 'loyaltyTiers':
                return (
                    <div className="rules-panel">
                        <div className="panel-head">
                            <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_LOYALTY_TIERS_REWARDS')}</h4>
                            <p className="panel-sub">{t('LOYALTY_PROGRAM_LOYALTY_TIERS_DESCRIPTION')}</p>
                        </div>

                        {/* Attractive tier list (responsive) */}
                        <div className="tier-list">
                            {activeMedalLevels.map((level, index) => (
                                <div className={`tier-item tier-${index}`} key={index}>
                                    <span className="tier-item__medal" aria-hidden="true">
                                        {TIER_MEDALS[index] || (
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                                <path d="M12 2.5l2.7 5.5 6 .9-4.35 4.24 1.03 6-5.38-2.83L6.62 19.13l1.03-6L3.3 8.9l6-.9z" />
                                            </svg>
                                        )}
                                    </span>
                                    <div className="tier-item__info">
                                        <span className="tier-item__name">{t(level.tier)}</span>
                                        <span className="tier-item__benefit">{t(level.extraBenefits)}</span>
                                    </div>
                                    <div className="tier-item__right">
                                        <span className="tier-item__points">
                                            {level.tier === 'LOYALTY_PROGRAM_IRON_LEVEL'
                                                ? `0 - ${formatAmount(level.pointsNeeded)}`
                                                : formatAmount(level.pointsNeeded)}
                                            <small> {t('LOYALTY_PROGRAM_POINTS_NEEDED')}</small>
                                        </span>
                                        <span className="tier-item__discount">{level.discount}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="panel-note">
                            <span className="note-highlight">{t('LOYALTY_PROGRAM_DISCOUNT_AUTO_APPLY')}</span> {t('LOYALTY_PROGRAM_DISCOUNT_AUTO_APPLY_TEXT')}
                        </p>
                        <p className="panel-note">
                            {t('LOYALTY_PROGRAM_DISCOUNT_LIMITATION')}.
                        </p>
                    </div>
                );
            case 'longTerm':
                return (
                    <div className="rules-panel">
                        <div className="panel-head">
                            <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_LONG_TERM_REWARDS_TITLE')}</h4>
                            <p className="panel-sub">{t('LOYALTY_PROGRAM_LONG_TERM_REWARDS_DESCRIPTION')}</p>
                        </div>
                        <div className="long-term-rewards-content">
                            <div className="reward-section">
                                <h5 className="reward-tier-title">{t('LOYALTY_PROGRAM_GOLD_REWARD_OPTIONS')}</h5>
                                <ul className="reward-list">
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_GOLD_GIFT_OPTION') }}></li>
                                    {/* <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_GOLD_ADVICE_OPTION') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_GOLD_BONUS_OPTION') }}></li> */}
                                </ul>
                            </div>
                            <div className="reward-section">
                                <h5 className="reward-tier-title">{t('LOYALTY_PROGRAM_PLATINUM_REWARD_OPTIONS')}</h5>
                                <ul className="reward-list">
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_PLATINUM_GIFT_OPTION') }}></li>
                                    {/* <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_PLATINUM_ADVICE_OPTION') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_PLATINUM_BONUS_OPTION') }}></li> */}
                                </ul>
                            </div>
                        </div>
                        {/* <p className="panel-note">{t('LOYALTY_PROGRAM_REWARD_NOTIFICATION')}</p> */}
                    </div>
                );
            case 'rules':
                return (
                    <div className="rules-panel">
                        <div className="panel-head">
                            <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_IMPORTANT_RULES_TITLE')}</h4>
                        </div>

                        {/* Desktop View */}
                        <div className="important-rules-content desktop-only">
                            <div className="rules-section">
                                <ul className="rules-list">
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_POINTS_AFTER_PAYMENT') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_REFERRAL_THRESHOLD') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_UNIQUE_REVIEWS') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_INDIVIDUAL_ONLY') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_TIER_DOWNGRADE') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_EMAIL_WARNING') }}></li>
                                </ul>
                            </div>
                            <div className="mt-3">
                                <h5 className="Heading_h5">{t("INACTIVITY_AND_DEACTIVATION")}</h5>
                                <p className="text-black-500">{t('INACTIVITY_AND_DEACTIVATION_DESCRIPTION')}</p>
                            </div>
                        </div>

                        <div className="important-rules-content mobile-only">
                            <div className="rules-section">
                                <ul className="rules-list">
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_POINTS_AFTER_PAYMENT') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_REFERRAL_THRESHOLD') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_UNIQUE_REVIEWS') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_INDIVIDUAL_ONLY') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_TIER_DOWNGRADE') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_EMAIL_WARNING') }}></li>
                                </ul>
                            </div>

                        </div>
                        {/* 
                        <div className="inactivity-section">
                            <h5 className="inactivity-title">{t('LOYALTY_PROGRAM_INACTIVITY_TITLE')}</h5>
                            <p className="inactivity-description" dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_INACTIVITY_DESCRIPTION') }}></p>
                        </div> */}
                    </div>
                );
            default:
                return renderHowToEarnContent();
        }
    };

    const handleTermsClick = () => {
        let pdfUrl: string;
        if (accountType === 2) {
            pdfUrl = isEnglish
                ? 'https://invozioprod.blob.core.windows.net/website-assets/pdf/ENLoyaltyProgramTermsBusinessClients.pdf'
                : 'https://invozioprod.blob.core.windows.net/website-assets/pdf/SVLojalitetsProgramVillkorFöretagskunder.pdf';
        } else {
            pdfUrl = isEnglish
                ? 'https://invozioprod.blob.core.windows.net/website-assets/pdf/InvozioLoyaltyProgramEnglish.pdf'
                : 'https://invozioprod.blob.core.windows.net/website-assets/pdf/LojalitetsprogrammetSwedish.pdf';
        }
        window.open(pdfUrl, '_blank');
    };

    return (
        <div className="loyalty-rules-root">
            <h3 className="rules-heading">
                <span className="rules-heading__icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 3l7 3v5.2c0 4.3-3 8.3-7 9.8-4-1.5-7-5.5-7-9.8V6l7-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        <path d="M9 12l2.1 2.1L15 10.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
                {t('LOYALTY_PROGRAM_RULES_TERMS')}
            </h3>

            <div className="rules-tabbar">
                <div className="rules-tabs-pills" role="tablist" ref={pillsRef}>
                    <span
                        className={`rules-pill-indicator${indicatorReady ? ' is-animated' : ''}`}
                        aria-hidden="true"
                        style={{ transform: `translateX(${indicator.left}px)`, width: `${indicator.width}px` }}
                    />
                    {tabs.map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={active}
                                className={`rules-pill-btn${active ? ' active' : ''}`}
                                onClick={() => handleTabChange(tab.key)}
                            >
                                <span className="label">{t(tab.label)}</span>
                            </button>
                        );
                    })}
                </div>
                <button className="terms-btn" type="button" onClick={handleTermsClick}>
                    <span className="label">{t('LOYALTY_PROGRAM_VIEW_TERMS_CONDITIONS')}</span>
                    <img src="/assets/svg/download-icon.svg" className="arrow-icon" alt="" aria-hidden="true" />
                </button>
            </div>

            <div className="rules-panel-wrapper" role="tabpanel">
                {isSwitching ? renderSkeleton() : renderContent()}
            </div>
        </div>
    );
};

export default LoyaltyRulesSection;
